import { Divider, Backdrop, Box, CircularProgress, Menu, MenuItem, Dialog, DialogTitle, DialogContent, TextField, Paper, Typography, Tooltip, Button, DialogActions } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import CompareIcon from '@mui/icons-material/Compare';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import TranslateIcon from '@mui/icons-material/Translate';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderIcon from '@mui/icons-material/Folder';
import HistoryIcon from '@mui/icons-material/History';
import SplitScreenIcon from '@mui/icons-material/Splitscreen';
import { ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import videoUploadService from '../../services/VideoUploadService';
import MoveDialog from '../dialogs/MoveDialog';
import ProgressStagesDialog from '../dialogs/ProgressStagesDialog';
import Text2TextDialog from '../dialogs/Text2TextDialog';
import VideoHistoryDialog from '../dialogs/VideoHistoryDialog';
import ExperienceSwitcher from '../ExperienceSwitcher';
import ModernFolderView from './ModernFolderView';
import { getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import UploadVideoDialog from '../subtitles/UploadVideoDialog';
import { LinkTypes } from '../../utils/constants';
import AssignVideoDialog from '../dialogs/AssignVideoDialog';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import { useAssignmentContext } from '../../contexts/AssignmentContext';
import { validateItemName } from '../../utils/formatUtils';
import SectionManagerDialog from '../dialogs/SectionManagerDialog';
import { isAdmin } from '../../utils/authUtils';
import { getVibrantIconColor } from '../../themes/VibrantTheme';
import FolderHistoryDialog from '../dialogs/FolderHistoryDialog';

const FolderBrowser = ({ setCanNavigateToEdit, darkMode, toggleDarkMode, themeVariant }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // State variables
    const [selectedFile, setSelectedFile] = useState('');
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [file, setFile] = useState('');
    const [videoName, setVideoName] = useState('');
    const [open, setOpen] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [linkError, setLinkError] = useState(false);
    const [isCompressed, setIsCompressed] = useState(false);
    const [isLogging, setIsLogging] = useState(false);
    const [selectOpenAI, setSelectOpenAI] = useState(true);
    const [bitRate, setBitRate] = useState('115');
    const [crfValue, setCRFValue] = useState('28');
    const [skipTranslationIsChecked, setSkipTranslationIsChecked] = useState(false);
    const [onlyTransliterationIsChecked, setOnlyTransliterationIsChecked] = useState(false);
    const [currentPath, setCurrentPath] = useState('/');
    const [linkType, setLinkType] = useState(LinkTypes.NONE);
    const [youtubeLinkURL, setYoutubeLinkURL] = useState('');
    const [googleLinkURL, setGoogleLinkURL] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [text2TextDialogOpen, setText2TextDialogOpen] = useState(false);
    const [selectedVideoForText2Text, setSelectedVideoForText2Text] = useState(null);
    const [progressStagesDialogOpen, setProgressStagesDialogOpen] = useState(false);
    const [selectedVideoForProgressStages, setSelectedVideoForProgressStages] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedVideoForHistory, setSelectedVideoForHistory] = useState(null);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [itemToMove, setItemToMove] = useState(null);
    const [assignVideoDialogOpen, setAssignVideoDialogOpen] = useState(false);
    const [selectedVideoForAssign, setSelectedVideoForAssign] = useState(null);
    const [folderHistoryDialogOpen, setFolderHistoryDialogOpen] = useState(false);
    const [selectedFolderForHistory, setSelectedFolderForHistory] = useState(null);

    // Add these state variables to track changes
    const [refreshFlag, setRefreshFlag] = useState(0);
    const [stagesDialogChangesMade, setStagesDialogChangesMade] = useState(false);
    const [historyDialogChangesMade, setHistoryDialogChangesMade] = useState(false);

    // Add state for folder creation dialog
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [folderDestinationPath, setFolderDestinationPath] = useState('/');
    const [folderPathDialogOpen, setFolderPathDialogOpen] = useState(false);

    // Add this new state for section manager dialog
    const [sectionManagerDialogOpen, setSectionManagerDialogOpen] = useState(false);
    const [selectedFileForSections, setSelectedFileForSections] = useState(null);

    // Use the assignment context for any assignment operations
    const { assignVideo } = useAssignmentContext();

    useEffect(() => {
        // Check if we're coming from the video edit page
        if (location.state?.fromVideoEdit) {
            // Check if a refresh is needed based on the breadcrumb navigation return
            const needsRefresh = location.state?.refreshNeeded || false;

            if (location.state?.folderPath) {
                const folderPath = location.state.folderPath;
                setCurrentPath(folderPath);
                loadFolderContents(folderPath);
            } else {
                const storedBreadcrumbs = localStorage.getItem('breadcrumbs');

                if (storedBreadcrumbs) {
                    try {
                        const breadcrumbArray = JSON.parse(storedBreadcrumbs);
                        if (Array.isArray(breadcrumbArray) && breadcrumbArray.length > 0) {
                            const folderPath = '/' + breadcrumbArray.join('/');
                            setCurrentPath(folderPath);
                            // Only refresh if explicitly needed or we don't yet know if changes were made
                            if (needsRefresh) {
                                loadFolderContents(folderPath);
                            } else {
                                // Just set the path, but don't reload
                                loadFolderContents(folderPath);
                            }
                            return;
                        }
                    } catch (err) {
                        console.error('Error parsing breadcrumbs:', err);
                    }
                }

                // Fallback to root if no breadcrumbs found
                setCurrentPath('/');
                loadFolderContents('/');
            }
        } else {
            loadFolderContents('/');
        }
    }, [location]);

    // Add a dependency-based effect for refreshing when flags change
    useEffect(() => {
        // Only refresh if explicitly needed via flags
        if (stagesDialogChangesMade || historyDialogChangesMade) {
            loadFolderContents(currentPath);
            // Reset the flags after refresh
            setStagesDialogChangesMade(false);
            setHistoryDialogChangesMade(false);
        }
    }, [refreshFlag, stagesDialogChangesMade, historyDialogChangesMade]);

    const fetchFileLanguages = (fileList) => {
        if (!fileList || fileList.length === 0) return;

        // Get all video IDs
        const videoIds = fileList
            .map(file => file.data )
            .filter(Boolean);

        if (videoIds.length === 0) return;

        setFileUploading(true);

        // Create promises that won't reject the whole chain
        const safePromises = videoIds.map(id => 
            videoCRUDServiceInstance.getVideoById(id)
                .then(response => ({ 
                    success: true, 
                    id: id, 
                    data: response.data 
                }))
                .catch(error => ({ 
                    success: false, 
                    id: id, 
                    error: error 
                }))
        );

        Promise.all(safePromises)
            .then(results => {
                // Log errors but continue processing successful responses
                results.forEach(result => {
                    if (!result.success) {
                        console.warn(`Failed to fetch data for video ID ${result.id}:`, result.error);
                    }
                });

                // Filter out failed requests and process only successful ones
                const successfulResponses = results.filter(result => result.success);
                
                // Process successful responses
                const languageData = successfulResponses.map(result => {
                    const videoData = result.data;

                    const languagesWithStatus = videoData.videoLanguages
                        ? videoData.videoLanguages.map(lang => ({
                            name: lang.languageName,
                            status: lang.status,
                            processStage: lang.processStage || 'UNKNOWN'
                        }))
                        : [];

                    return {
                        id: videoData.id,
                        videoLanguages: videoData.videoLanguages || [],
                        languages: languagesWithStatus,
                        videoName: videoData.videoName || '',
                        compressedUrl: videoData.compressedUrl || '',
                        updatedBy: videoData.updatedBy || '',
                        updatedAt: videoData.lastModifiedDate || '',
                        duration: videoData.duration || '',
                    };
                });

                // Update files with video data
                const updatedFiles = fileList.map(file => {
                    const videoId = file.data;
                    const matchingData = languageData.find(item => item.id === videoId);

                    if (matchingData) {
                        return {
                            ...file,
                            id: matchingData.id,
                            languages: matchingData.languages || [],
                            videoLanguages: matchingData.videoLanguages || [],
                            videoName: matchingData.videoName || file.name || '',
                            compressedUrl: matchingData.compressedUrl || '',
                            updatedBy: matchingData.updatedBy || '',
                            updatedAt: matchingData.updatedAt || '',
                            duration: matchingData.duration || '',
                        };
                    }

                    return file;
                });

                setFiles(updatedFiles);
            })
            .catch(error => {
                // This shouldn't happen since we wrapped each promise to prevent rejection
                console.error('Unexpected error in fetchFileLanguages:', error);
            })
            .finally(() => {
                setFileUploading(false);
            });
    };

    const loadFolderContents = useCallback((path) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            console.error('No user data found in localStorage');
            return;
        }
        
        setFileUploading(true);

        fileSystemAccessServiceInstance
            .getFolderContents(path, userData.email, userData.access)
            .then((response) => {
                const folderItems = response.data.filter((e) => e.isFolder === true);
                const fileItems = response.data.filter((e) => e.isFolder === false);

                setFolders(folderItems);
                setFiles(fileItems);

                // Fetch languages for all files
                if (fileItems.length > 0) {
                    fetchFileLanguages(fileItems);
                } else {
                    setFileUploading(false);
                }
            })
            .catch((e) => {
                console.error('Error loading folder contents:', e);
                setFileUploading(false);
            });
    }, [fetchFileLanguages]);

    const handleFolderClick = useCallback((folder) => {
        if (folder.path === undefined || folder.path === "") {
            folder.path = currentPath === '/' ? currentPath + folder.name : currentPath + '/' + folder.name;
        }
        setCurrentPath(folder.path);
        loadFolderContents(folder.path);
    }, [currentPath, loadFolderContents]);

    const handleFileClick = useCallback(async (fileData) => {
        localStorage.removeItem('breadcrumbs');
        localStorage.removeItem('videoProps');
        localStorage.removeItem('subtitle');
        localStorage.removeItem('timelineProps');
        localStorage.removeItem('checkedForDelete');
        localStorage.removeItem('lang');

        setFileUploading(true);

        try {
            const userData = JSON.parse(localStorage.getItem('authUser'));

            let videoId = fileData.data;
            
            if (!videoId) {
                console.log("Error could not determine video ID");
                return;
            }

            const response = await videoCRUDServiceInstance.getVideoById(
                videoId,
                userData.email,
                userData.access
            );

            const fullVideoData = response.data;

            localStorage.setItem('videoProps', JSON.stringify(fullVideoData));

            const breadcrumbs = currentPath.split('/').filter(Boolean);
            localStorage.setItem('breadcrumbs', JSON.stringify(breadcrumbs));

            setFileUploading(false);

            if (fullVideoData.videoLanguages) {
                const englishLanguage = fullVideoData.videoLanguages.find(lang => lang.languageName === 'English');
                if (englishLanguage && englishLanguage.status === 'READY' && fullVideoData.compressedUrl) {
                    setCanNavigateToEdit(true);
                    navigate('/videos/edit', { state: { videoData: fullVideoData } });
                } else {
                    setCanNavigateToEdit(false);
                    Swal.fire({
                        title: 'Video not ready',
                        text: 'The video is not ready for editing yet. Please check back later.',
                        icon: 'warning',
                        confirmButtonText: 'Close',
                    });
                    return;
                }
            } else {
                setCanNavigateToEdit(false);
                Swal.fire({
                    title: 'Video not ready',
                    text: 'The video is not ready for editing yet. Please check back later.',
                    icon: 'warning',
                    confirmButtonText: 'Close',
                });
                return;
            }

        } catch (error) {
            console.error('Error fetching video details:', error);
            setFileUploading(false);

            Swal.fire({
                title: 'Error',
                text: 'Failed to load video details. Please try again.',
                icon: 'error',
                confirmButtonText: 'Close',
            });
        }
    }, [currentPath, navigate, setCanNavigateToEdit]);

    const createFolder = async (folderName, path) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (userData) {
            return fileSystemAccessServiceInstance.createFolder(
                path,
                folderName,
                userData.email,
                userData.access
            );
        }
    };

    const [folderNameTouched, setFolderNameTouched] = useState(false);

    // Replace multiple separate state updates with batched updates
    const handleCreateFolder = useCallback(() => {
        // Batch these state updates
        setNewFolderName('');
        setFolderDestinationPath(currentPath);
        setCreateFolderDialogOpen(true);
        setFolderNameTouched(false);
    }, [currentPath]);

    const handleCreateFolderSubmit = async () => {
        if (!newFolderName) return;
        
        try {
            setFileUploading(true); // Show loading indicator
            
            await createFolder(newFolderName, folderDestinationPath);
            
            // Explicitly close the dialog first
            setCreateFolderDialogOpen(false);
            
            // Wait a moment to ensure dialog is fully closed
            setTimeout(() => {
                // If folder was created in current path, just refresh
                if (folderDestinationPath === currentPath) {
                    loadFolderContents(currentPath);
                } else {
                    // If created in a different path, navigate to that path
                    setCurrentPath(folderDestinationPath);
                    loadFolderContents(folderDestinationPath);
                }
                
                setFileUploading(false); // Hide loading indicator
                
                // Show success message after dialog is closed
                Swal.fire({
                    icon: 'success',
                    title: 'Folder Created',
                    text: `Folder "${newFolderName}" created successfully`,
                    timer: 1500,
                    showConfirmButton: false
                });
            }, 300);
        } catch (error) {
            setFileUploading(false);
            console.error('Error creating folder:', error);
            
            // Make sure dialog is closed even on error
            setCreateFolderDialogOpen(false);
            
            setTimeout(() => {
                Swal.fire('Error', 'Failed to create folder', 'error');
            }, 300);
        }
    };

    const handleShowFolderHistory = (folder) => {
        if (!folder) return;
        
        setSelectedFolderForHistory(folder);
        setFolderHistoryDialogOpen(true);
        handleMenuClose();
    };

    // Improved error handling in the handleRename function
    const handleRename = async (item, newName) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) return Promise.reject(new Error('No user data found'));

        try {
            // Handle based on whether it's a folder or file
            if (item.isFolder) {
                const itemPath = `${currentPath}/${item.name}`.replace(/\/\//g, '/');
                try {
                    await fileSystemAccessServiceInstance.renameFolder(
                        itemPath,
                        newName,
                        userData.email,
                        userData.access
                    );
                    
                    // Refresh folder contents and show success message
                    loadFolderContents(currentPath);
                    Swal.fire({
                        icon: 'success',
                        title: 'Renamed',
                        text: `"${item.name}" renamed to "${newName}" successfully`,
                        timer: 1500,
                        showConfirmButton: false
                    });
                    
                    return true;
                } catch (error) {
                    console.error('Rename folder error:', error);
                    
                    // Handle specific error cases
                    if (error.response) {
                        // Handle 404 error explicitly
                        if (error.response.status === 404) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Rename Error',
                                text: `Not able to rename, make sure there is no folder named "${newName}" already in this location.`,
                                confirmButtonText: 'OK'
                            });
                            
                            // Refresh to show current state
                            loadFolderContents(currentPath);
                            return false;
                        }
                        
                        // Handle folder already exists
                        if (error.response.data && 
                            (error.response.data.errorCode === 'FOLDER_EXISTS' || 
                            error.response.data.message?.includes('already exists'))) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Name Already Exists',
                                text: `A folder named "${newFolderName}" already exists in this location.`,
                                confirmButtonText: 'OK'
                            });
                            return false;
                        }
                    }
                    
                    // Generic error handling
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to rename folder',
                        text: error.response?.data?.message || error.message || 'An unknown error occurred',
                        confirmButtonText: 'OK'
                    });
                    
                    return Promise.reject(error);
                }
            } else {
                // Video file renaming logic
                const videoId = item.data;
                if (!videoId) {
                    return Promise.reject(new Error('Could not determine video ID'));
                }

                try {
                    await videoCRUDServiceInstance.rename(
                        videoId,
                        currentPath,
                        userData.email,
                        userData.access,
                        newName
                    );

                    loadFolderContents(currentPath);
                    Swal.fire({
                        icon: 'success',
                        title: 'Renamed',
                        text: `"${item.videoName || item.name}" renamed to "${newName}" successfully`,
                        timer: 1500,
                        showConfirmButton: false
                    });

                    return true;
                } catch (error) {
                    console.error('Rename video error:', error);
                    
                    // Handle specific error cases
                    if (error.response) {
                        // Handle 404 error explicitly
                        if (error.response.status === 404) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Video Not Found',
                                text: `The video "${item.videoName || item.name}" could not be found. It may have been deleted or moved.`,
                                confirmButtonText: 'OK'
                            });
                            
                            // Refresh to show current state
                            loadFolderContents(currentPath);
                            return false;
                        }
                        
                        // Handle video name already exists
                        if (error.response.data && 
                            (error.response.data.errorCode === 'FILE_EXISTS' || 
                            error.response.data.message?.includes('already exists'))) {
                            Swal.fire({
                                icon: 'error',
                                title: 'Name Already Exists',
                                text: `A video named "${newFolderName}" already exists in this location.`,
                                confirmButtonText: 'OK'
                            });
                            return false;
                        }
                    }
                    
                    // Generic error handling
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed to rename video',
                        text: error.response?.data?.message || error.message || 'An unknown error occurred',
                        confirmButtonText: 'OK'
                    });
                    
                    return Promise.reject(error);
                }
            }
        } catch (error) {
            console.error('Rename error:', error);
            
            // Fallback error handling
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred during the rename operation',
                confirmButtonText: 'OK'
            });
            
            return Promise.reject(error);
        }
    };

    const handleDelete = async (item) => {
        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) return false;

        const itemPath = `${currentPath}/${item.name}`.replace(/\/\//g, '/');
        setFileUploading(true);

        try {
            if (item.isFolder) {
                await fileSystemAccessServiceInstance.delete(
                    itemPath,
                    userData.email,
                    userData.access
                );
            } else {
                const videoId = item.data;
                if (!videoId) {
                    throw new Error('Could not determine video ID');
                }

                await videoCRUDServiceInstance.delete(
                    videoId,
                    currentPath,
                    userData.email,
                    userData.access
                );
            }

            loadFolderContents(currentPath);
            Swal.fire({
                icon: 'success',
                title: 'Deleted',
                text: `${item.name} was moved to trash successfully`,
                timer: 1500,
                showConfirmButton: false
            });

            return true;

        } catch (error) {
            console.error('Delete error:', error);

            // Special handling for non-empty folders
            if (item.isFolder &&
                error.response &&
                error.response.data &&
                error.response.data.stringMap &&
                error.response.data.stringMap.errorCode === 'FOLDER_NOT_EMPTY') {

                const itemCount = error.response.data.stringMap.itemCount || 'multiple';
                setFileUploading(false);

                const forceResult = await Swal.fire({
                    title: 'Folder is not empty',
                    text: `This folder contains ${itemCount} item(s). Do you want to move all contents to trash?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, move everything to trash!',
                    cancelButtonText: 'Cancel'
                });

                if (forceResult.isConfirmed) {
                    setFileUploading(true);

                    try {
                        await fileSystemAccessServiceInstance.delete(
                            itemPath,
                            userData.email,
                            userData.access,
                            true // Force parameter
                        );

                        loadFolderContents(currentPath);
                        Swal.fire({
                            icon: 'success',
                            title: 'Deleted',
                            text: `${item.name} and its contents were moved to trash successfully`,
                            timer: 1500,
                            showConfirmButton: false
                        });

                        return true;
                    } catch (forceError) {
                        console.error('Force delete error:', forceError);
                        setFileUploading(false);
                        Swal.fire(
                            'Error',
                            'Failed to trash folder: ' + (forceError.response?.data?.message || 'Unknown error'),
                            'error'
                        );
                        throw forceError;
                    }
                } else {
                    setFileUploading(false);
                    return false;
                }
            } else {
                setFileUploading(false);

                Swal.fire(
                    'Error',
                    'Failed to trash: ' + (error.response?.data?.message || 'Unknown error'),
                    'error'
                );
                throw error;
            }
        }
    };

    const handleCreateVideo = (path) => {
        setOpen(true);
        setFile('');
        setVideoName('');
        setGoogleLinkURL('');
        setYoutubeLinkURL('');
        setLinkError(false);
        setIsCompressed(false);
        setIsLogging(false);
        setSelectOpenAI(true);
        setSkipTranslationIsChecked(false);
        setOnlyTransliterationIsChecked(false);
        setBitRate('115');
        setCRFValue('28');
        setCurrentPath(path);
    };

    const handleLinkTypeSelect = (l) => {
        if (l === 'gdrive') {
            setLinkType(LinkTypes.GOOGLE_DRIVE);
        } else if (l === 'youtube') {
            setLinkType(LinkTypes.YOUTUBE);
        } else if (l === 'blob') {
            setLinkType(LinkTypes.BLOB);
        } else if (l === 'local') {
            setLinkType(LinkTypes.LOCAL_FILE);
        }
    };

    const uploadVideo = async (linkType, destinationPath = currentPath) => {
        return videoUploadService.uploadVideo(linkType, {
            setFileUploading,
            googleLinkURL,
            youtubeLinkURL,
            videoName,
            folderPath: destinationPath, // Use the passed in path or default to currentPath
            isCompressed,
            selectOpenAI,
            isLogging,
            skipTranslationIsChecked,
            onlyTransliterationIsChecked,
            bitRate,
            crfValue,
            setOpen,
            selectedFile,
            onSuccess: () => {
                // If destination path is different from current path, navigate to it
                if (destinationPath !== currentPath) {
                    setCurrentPath(destinationPath);
                }
                loadFolderContents(destinationPath);
            }
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleMenuOpen = (event, item) => {
        setMenuAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedItem(null);
    };

    const handleShowProgressStages = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForProgressStages(videoId);
        setProgressStagesDialogOpen(true);
    };

    const handleShowHistory = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForHistory(videoId);
        setHistoryDialogOpen(true);
    };

    const handleRegenerateFile = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        Swal.fire({
            title: 'Regenerate Video Processing',
            text: 'This will restart the processing of this video. Do you want to continue?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, regenerate it',
            cancelButtonText: 'Cancel',
            showLoaderOnConfirm: true,
            preConfirm: () => {
                return videoUploadService.regenerate(videoId, file.videoName)
                    .then(response => {
                        return response;
                    })
                    .catch(error => {
                        Swal.showValidationMessage(
                            `Request failed: ${error.response?.data?.message || error.message}`
                        );
                    });
            }
        }).then(result => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Regeneration Started',
                    text: 'The video processing has been restarted.',
                    timer: 1500,
                    showConfirmButton: false
                });
                loadFolderContents(currentPath);
            }
        });
    };

    // Add this function where other handlers are defined
    const handleViewMultiColumn = (file) => {
        const videoId = file.data;
        
        if (!videoId) {
            Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Could not determine video ID'
            });
            return;
        }
        
        // Store video props in localStorage for the MultiColumnSubtitleViewer to use
        localStorage.setItem('videoProps', JSON.stringify(file));
        
        // Navigate to the multi-column viewer
       try {
            // Navigate to the multi-column viewer
            navigate(`/multi-column-viewer/${videoId}`);
        } catch (error) {
            console.error("Navigation error:", error);
            Swal.fire({
                icon: 'error',
                title: 'Navigation Error',
                text: 'Failed to navigate to multi-column view'
            });
        }
        
        // Close the context menu
        handleMenuClose();
    };

    const handleAddLanguages = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForText2Text(videoId);
        setText2TextDialogOpen(true);
    };

    const handleText2TextDialogClose = () => {
        setText2TextDialogOpen(false);
        fetchFileLanguages(files);
    };

    const handleText2TextSuccess = (result) => {
        console.log('Translation initiated:', result);
    };

    const handleMoveItem = (item) => {
        if (!item) return;

        if (item.isFolder) {
            setItemToMove({
                isFolder: true,
                path: item.path || `${currentPath}/${item.name}`.replace(/\/\//g, '/'),
                name: item.name,
                item: item
            });
        } else {
            const videoId = item.data;

            if (!videoId) {
                Swal.fire('Error', 'Could not determine video ID', 'error');
                return;
            }

            setItemToMove({
                isFolder: false,
                id: videoId,
                name: item.videoName || item.name,
                item: item
            });
        }

        setMoveDialogOpen(true);
    };

    const handleMoveToFolder = (destinationPath) => {
        if (!itemToMove) {
            Swal.fire('Error', 'No item selected for moving', 'error');
            return;
        }

        setFileUploading(true);

        const userData = JSON.parse(localStorage.getItem('authUser'));
        if (!userData) {
            Swal.fire('Error', 'User authentication data not found', 'error');
            return;
        }

        fileSystemAccessServiceInstance.moveItem(
            itemToMove,
            currentPath,
            destinationPath,
            userData.email,
            userData.access
        )
            .then(() => {
                setFileUploading(false);
                Swal.fire({
                    icon: 'success',
                    title: `${itemToMove.isFolder ? 'Folder' : 'File'} Moved`,
                    text: `The ${itemToMove.isFolder ? 'folder' : 'file'} has been moved successfully.`,
                    timer: 1500,
                    showConfirmButton: false
                });
                loadFolderContents(currentPath);
            })
            .catch(error => {
                setFileUploading(false);
                console.error(`Error moving ${itemToMove.isFolder ? 'folder' : 'file'}:`, error);

                if (error.response?.data?.errorCode === 'TARGET_EXISTS') {
                    Swal.fire({
                        title: 'Target Already Exists',
                        text: 'Would you like to replace the existing item?',
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: 'Yes, replace',
                        cancelButtonText: 'No, keep both'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            fileSystemAccessServiceInstance.moveItem(
                                itemToMove,
                                currentPath,
                                destinationPath,
                                userData.email,
                                userData.access,
                                true // force replacement
                            )
                                .then(() => {
                                    setFileUploading(false);
                                    Swal.fire({
                                        icon: 'success',
                                        title: 'Item Replaced',
                                        text: 'The item has been moved and replaced the existing one.',
                                        timer: 1500,
                                        showConfirmButton: false
                                    });
                                    loadFolderContents(currentPath);
                                })
                                .catch(replaceError => {
                                    setFileUploading(false);
                                    console.error('Error replacing item:', replaceError);
                                    Swal.fire('Error', replaceError.response?.data?.message || 'Failed to replace item', 'error');
                                });
                        }
                    });
                } else {
                    Swal.fire('Error', error.response?.data?.message || `Failed to move ${itemToMove.isFolder ? 'folder' : 'file'}`, 'error');
                }
            });
    };

    const handleShowAssignDialog = (file) => {
        const videoId = file.data;

        if (!videoId) {
            Swal.fire('Error', 'Could not determine video ID', 'error');
            return;
        }

        setSelectedVideoForAssign({
            ...file,
            id: videoId
        });
        setAssignVideoDialogOpen(true);
    };

    // Add this new handler function
    const handleShowSectionManager = (file) => {
        const videoId = file.data;

        if (!videoId) {
            // Show error if no video ID
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Could not determine video ID'
            });
            return;
        }

        setSelectedFileForSections(file);
        setSectionManagerDialogOpen(true);
        handleMenuClose(); // Close the context menu
    };

    const handleNavigateToTrashBin = () => {
        navigate('/trash');
    };

    return (
        <>
            <Backdrop sx={{ color: '#FFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={fileUploading}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <ModernFolderView
                folders={folders}
                files={files}
                currentPath={currentPath}
                onFolderClick={handleFolderClick}
                onFileClick={handleFileClick}
                onCreateFolder={handleCreateFolder}
                onRename={handleRename}
                onDelete={handleDelete}
                onCreateVideo={handleCreateVideo}
                onMenuOpen={handleMenuOpen}
                onRefresh={() => loadFolderContents(currentPath)}
                videoExperience={fileSystemAccessServiceInstance.getVideoExperience && fileSystemAccessServiceInstance.getVideoExperience()}
                themeVariant={themeVariant}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
            />

            <Box
                sx={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    padding: '4px',
                    backdropFilter: 'blur(5px)',
                }}
            >
                <Tooltip title="Trash">
                    <Button
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        onClick={handleNavigateToTrashBin}
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        Trash
                    </Button>
                </Tooltip>
            </Box>

            {/* Dialogs and menus */}
            <UploadVideoDialog
                open={open}
                handleClose={handleClose}
                videoName={videoName}
                setVideoName={setVideoName}
                googleLinkURL={googleLinkURL}
                setGoogleLinkURL={setGoogleLinkURL}
                youtubeLinkURL={youtubeLinkURL}
                setYoutubeLinkURL={setYoutubeLinkURL}
                isLinkError={linkError}
                getLanguagesForCreateVideo={getLanguagesForCreateVideo}
                isCompressed={isCompressed}
                setIsCompressed={setIsCompressed}
                selectOpenAI={selectOpenAI}
                setSelectOpenAI={setSelectOpenAI}
                isLogging={isLogging}
                setIsLogging={setIsLogging}
                skipTranslationIsChecked={skipTranslationIsChecked}
                setSkipTranslationIsChecked={setSkipTranslationIsChecked}
                onlyTransliterationIsChecked={onlyTransliterationIsChecked}
                setOnlyTransliterationIsChecked={setOnlyTransliterationIsChecked}
                bitRate={bitRate}
                setBitRate={setBitRate}
                crfValue={crfValue}
                setCRFValue={setCRFValue}
                uploadVideo={uploadVideo}
                setOpen={setOpen}
                linkType={linkType}
                handleLinkTypeSelect={handleLinkTypeSelect}
                setLinkError={setLinkError}
                onFileSelect={setSelectedFile}
                fileName={selectedFile}
                folderPath={currentPath} // Pass current path
                darkMode={darkMode}
                themeVariant={themeVariant}
            />

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                data-testid="folder-context-menu"
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        width: 400,
                        '& .MuiMenuItem-root': {
                            borderRadius: '4px',
                            mx: 0.5,
                            my: 0.3,
                        }
                    }
                }}
            >
                {/* ORGANIZATION GROUP - Change to purple */}
                <Box sx={{ 
                    px: 2, 
                    py: 0.5, 
                    fontSize: '0.75rem', 
                    color: 'text.secondary',
                    fontWeight: 'medium',
                    borderLeft: '3px solid', 
                    borderColor: 'purple.main', // Change to purple
                    backgroundColor: 'rgba(128, 0, 128, 0.05)', // Light purple background
                    borderRadius: '0 4px 4px 0'
                }}>
                    Organization
                </Box>
                
                <MenuItem data-testid="menu-rename" onClick={() => {
                    if (selectedItem) {
                        Swal.fire({
                            title: 'Rename',
                            input: 'text',
                            inputValue: selectedItem.isFolder ? selectedItem.name : selectedItem.videoName,
                            showCancelButton: true,
                            confirmButtonText: 'Rename',
                            inputValidator: (value) => {
                                // First check if the name is valid using our utility
                                const validationError = validateItemName(
                                    value, 
                                    selectedItem.isFolder ? 'folder' : 'file'
                                );
                                
                                if (validationError) {
                                    return validationError;
                                }
                                
                                // Then check if name is unchanged
                                const currentName = selectedItem.isFolder ? selectedItem.name : (selectedItem.videoName || selectedItem.name);
                                if (value === currentName) {
                                    return 'Please enter a different name';
                                }
                            }
                        }).then((result) => {
                            if (result.isConfirmed && result.value) {
                                handleRename(selectedItem, result.value);
                            }
                            handleMenuClose();
                        });
                    } else {
                        handleMenuClose();
                    }
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>

                <MenuItem data-testid="menu-move" onClick={() => {
                    handleMoveItem(selectedItem);
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <DriveFileMoveIcon fontSize="small" color="secondary" />
                    </ListItemIcon>
                    <ListItemText>Move to Folder</ListItemText>
                </MenuItem>

                {/* FILE-SPECIFIC OPTIONS - Only show for files, not folders */}
                
                        <Divider sx={{ my: 1 }} />
                        
                        {/* STATUS GROUP - Success green with left border */}
                        <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            fontSize: '0.75rem', 
                            color: 'text.secondary',
                            fontWeight: 'medium',
                            borderLeft: '3px solid', 
                            borderColor: 'success.main' // Green border
                        }}>
                            Status & Progress
                        </Box>
                    {selectedItem && selectedItem.isFolder && (
                        <>
                        <MenuItem
                            data-testid="menu-folder-history"
                            onClick={() => {
                                handleShowFolderHistory(selectedItem);
                            }}
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>View Folder History</ListItemText>
                        </MenuItem>
                        </>
                    )}
                    {selectedItem && !selectedItem.isFolder && (
                    <>
                        <MenuItem 
                            data-testid="menu-progress-stages"
                            onClick={() => {
                                handleShowProgressStages(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <TimelineIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See Progress Stages</ListItemText>
                        </MenuItem>

                        <MenuItem 
                            data-testid="menu-history"
                            onClick={() => {
                                handleShowHistory(selectedItem);
                                handleMenuClose();
                            }}  
                        >
                            <ListItemIcon>
                                <HistoryIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>See History</ListItemText>
                        </MenuItem>

                        <MenuItem onClick={() => handleViewMultiColumn(selectedItem)}>
                            <ListItemIcon>
                                <CompareIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Subtitle Version Viewer</ListItemText>
                        </MenuItem>

                        <Divider sx={{ my: 1 }} />
                        
                        {/* SUBTITLE GROUP - Info blue with left border */}
                        <Box sx={{ 
                            px: 2, 
                            py: 0.5, 
                            fontSize: '0.75rem', 
                            color: 'text.secondary',
                            fontWeight: 'medium',
                            borderLeft: '3px solid', 
                            borderColor: 'info.main' // Blue border
                        }}>
                            Subtitle Management
                        </Box>
                        
                        <MenuItem 
                            data-testid="menu-manage-sections"
                            onClick={() => {
                                handleShowSectionManager(selectedItem);
                            }}
                        >
                            <ListItemIcon>
                                <SplitScreenIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText>Manage Subtitle Sections</ListItemText>
                            <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        fontSize: '0.65rem', 
                                        fontStyle: 'italic',
                                        ml: 1,
                                        color: isAdmin() ? 'primary.main' : 'text.secondary',
                                        opacity: isAdmin() ? 0.7 : 0.9,
                                        bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        px: isAdmin() ? 0.8 : 0,
                                        py: isAdmin() ? 0.2 : 0,
                                        borderRadius: isAdmin() ? 1 : 0,
                                    }}
                                >
                                    Coming Soon ...
                            </Typography>
                        </MenuItem>

                        <MenuItem 
                            data-testid="menu-text2text"
                            onClick={() => {
                                handleAddLanguages(selectedItem);
                                handleMenuClose();
                            }}
                        >
                            <ListItemIcon>
                                <TranslateIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <ListItemText>Text2Text Translations</ListItemText>
                        </MenuItem>
                        
                        {/* Assign Video - with admin badge for all users */}
                        <MenuItem 
                            data-testid="menu-assign"
                            onClick={() => {
                                if (isAdmin()) {
                                    handleShowAssignDialog(selectedItem);
                                    handleMenuClose();
                                }
                            }}
                            disabled={!isAdmin()}
                            sx={{
                                opacity: isAdmin() ? 1 : 0.6,
                            }}
                        >
                            <ListItemIcon>
                                <AssignmentIcon fontSize="small" color="info" />
                            </ListItemIcon>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <ListItemText>Assign Video</ListItemText>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        fontSize: '0.65rem', 
                                        fontStyle: 'italic',
                                        ml: 1,
                                        color: isAdmin() ? 'primary.main' : 'text.secondary',
                                        opacity: isAdmin() ? 0.7 : 0.9,
                                        bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                        px: isAdmin() ? 0.8 : 0,
                                        py: isAdmin() ? 0.2 : 0,
                                        borderRadius: isAdmin() ? 1 : 0,
                                    }}
                                >
                                    admin
                                </Typography>
                            </Box>
                        </MenuItem>
                        
                        
                    </>
                )}

                {/* DANGER ZONE - Error red with more visual emphasis */}
                <Divider sx={{ my: 1 }} />

                <Box sx={{ 
                    px: 2, 
                    py: 0.5, 
                    fontSize: '0.75rem', 
                    color: 'error.main',
                    fontWeight: 'bold', // Make it bolder
                    borderLeft: '3px solid', 
                    borderColor: 'error.main',
                    backgroundColor: 'rgba(211, 47, 47, 0.08)', // Light red background
                    borderRadius: '0 4px 4px 0' // Round the right corners
                }}>
                    Are you sure?
                </Box>

                {/* Only show Regenerate for files, not folders, and only for admins */}
                {selectedItem && !selectedItem.isFolder && (
                    <MenuItem 
                        data-testid="menu-regenerate"
                        onClick={() => {
                            if (isAdmin()) {
                                handleRegenerateFile(selectedItem);
                                handleMenuClose();
                            }
                        }}
                        disabled={!isAdmin()}
                        sx={{
                            opacity: isAdmin() ? 1 : 0.6,
                        }}
                    >
                        <ListItemIcon>
                            <RefreshIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <ListItemText>Regenerate</ListItemText>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontSize: '0.65rem', 
                                    fontStyle: 'italic',
                                    ml: 1,
                                    color: isAdmin() ? 'primary.main' : 'text.secondary',
                                    opacity: isAdmin() ? 0.7 : 0.9,
                                    bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                    px: isAdmin() ? 0.8 : 0,
                                    py: isAdmin() ? 0.2 : 0,
                                    borderRadius: isAdmin() ? 1 : 0,
                                }}
                            >
                                admin
                            </Typography>
                        </Box>
                    </MenuItem>
                )}

                <MenuItem 
                    data-testid="menu-delete" 
                    onClick={() => {
                        if (selectedItem && isAdmin()) {
                            Swal.fire({
                                title: 'Are you sure?',
                                text: `Do you want to move "${selectedItem.name}" to Trash?`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Move to Trash',
                                confirmButtonColor: '#d33'
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    handleDelete(selectedItem)
                                        .catch(() => {
                                            // Error handled inside handleDelete
                                        })
                                        .finally(() => {
                                            handleMenuClose();
                                        });
                                } else {
                                    handleMenuClose();
                                }
                            });
                        } else {
                            handleMenuClose();
                        }
                    }}
                    disabled={!isAdmin()}
                    sx={{ 
                        color: 'error.main',
                        opacity: isAdmin() ? 1 : 0.6,
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <ListItemText>Move to Trash</ListItemText>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                fontSize: '0.65rem', 
                                fontStyle: 'italic',
                                ml: 1,
                                color: isAdmin() ? 'primary.main' : 'text.secondary',
                                opacity: isAdmin() ? 0.7 : 0.9,
                                bgcolor: isAdmin() ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                                px: isAdmin() ? 0.8 : 0,
                                py: isAdmin() ? 0.2 : 0,
                                borderRadius: isAdmin() ? 1 : 0,
                            }}
                        >
                            admin
                        </Typography>
                    </Box>
                </MenuItem>
            </Menu>

            <Text2TextDialog
                open={text2TextDialogOpen}
                onClose={handleText2TextDialogClose}
                videoId={selectedVideoForText2Text}
                onSuccess={handleText2TextSuccess}
                folderPath={currentPath}
            />
            <ProgressStagesDialog
                open={progressStagesDialogOpen}
                onClose={() => {
                    setProgressStagesDialogOpen(false);
                    // If changes were made, schedule a refresh
                    if (stagesDialogChangesMade) {
                        setRefreshFlag(prev => prev + 1);
                    }
                }}
                videoId={selectedVideoForProgressStages}
                folderPath={currentPath}
                onChangesMade={() => setStagesDialogChangesMade(true)}
            />
            <VideoHistoryDialog
                open={historyDialogOpen}
                onClose={() => {
                    setHistoryDialogOpen(false);
                    // If changes were made, schedule a refresh
                    if (historyDialogChangesMade) {
                        setRefreshFlag(prev => prev + 1);
                    }
                }}
                videoId={selectedVideoForHistory}
                folderPath={currentPath}
                onChangesMade={() => setHistoryDialogChangesMade(true)}
            />
            <MoveDialog
                open={moveDialogOpen}
                onClose={() => setMoveDialogOpen(false)}
                onMove={handleMoveToFolder}
                fileName={itemToMove?.name}
                currentPath={currentPath}
                isFolder={itemToMove?.isFolder || false}
            />
            <AssignVideoDialog
                open={assignVideoDialogOpen}
                onClose={() => setAssignVideoDialogOpen(false)}
                videoInfo={selectedVideoForAssign}
            />
            <Dialog
                open={createFolderDialogOpen}
                onClose={() => setCreateFolderDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
                data-testid="create-folder-dialog"
            >
                <DialogTitle>Create New Folder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="folderName"
                        label="Folder Name"
                        type="text"
                        fullWidth
                        value={newFolderName}
                        onChange={(e) => {
                            setNewFolderName(e.target.value);
                            // Only mark as touched if there's actually a value
                            if (!folderNameTouched) {
                                setFolderNameTouched(true);
                            }
                        }}
                        
                        error={folderNameTouched && !!validateItemName(newFolderName, 'folder')}
                        helperText={folderNameTouched ? validateItemName(newFolderName, 'folder') || '' : ''}
                        data-testid="folder-name-input"
                    />
                    
                    <Box sx={{ mt: 3, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Destination:
                        </Typography>
                        <Paper 
                            variant="outlined" 
                            sx={{ 
                                p: 1.5, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                borderRadius: 1
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FolderIcon 
                                    sx={{ 
                                        color: themeVariant === 'vibrant' 
                                            ? getVibrantIconColor('folder', darkMode) 
                                            : 'primary.main',
                                        fontSize: '1.75rem'
                                    }} 
                                    color="primary" 
                                    sx={{ mr: 1 }} 
                                />
                                <Typography 
                                    variant="body2" 
                                    fontWeight="medium"
                                    data-testid="folder-destination-path"
                                >
                                    {folderDestinationPath === '/' ? 'Home' : folderDestinationPath}
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setFolderPathDialogOpen(true)}
                                data-testid="change-destination-button"
                                themeVariant={themeVariant}
                                darkMode={darkMode}
                            >
                                Change
                            </Button>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setCreateFolderDialogOpen(false)}
                        data-testid="cancel-folder-button"
                        variant="outlined"
                        color="secondary"
                        themeVariant={themeVariant}
                        darkMode={darkMode}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateFolderSubmit}
                        variant="outlined"
                        color="primary"
                        disabled={!newFolderName.trim() || !!validateItemName(newFolderName, 'folder')}
                        data-testid="create-folder-button"
                        themeVariant={themeVariant}
                        darkMode={darkMode}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <PathSelectorDialog
                open={folderPathDialogOpen}
                onClose={() => setFolderPathDialogOpen(false)}
                onSelect={(path) => {
                    setFolderDestinationPath(path);
                    setFolderPathDialogOpen(false);
                }}
                currentPath={folderDestinationPath}
                title="Select Folder Destination"
                data-testid="path-selector-dialog"
            />
            <SectionManagerDialog
                open={sectionManagerDialogOpen}
                onClose={() => setSectionManagerDialogOpen(false)}
                videoId={selectedFileForSections?.id || selectedFileForSections?.data || selectedFileForSections?._id}
            />

            <FolderHistoryDialog
                open={folderHistoryDialogOpen}
                onClose={() => setFolderHistoryDialogOpen(false)}
                folderId={selectedFolderForHistory?.id}
                folderName={selectedFolderForHistory?.name}
            />
        </>
    );
};

export default FolderBrowser;